<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" />

      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="float-end">
          <b-button
            v-if="id && !showDetailSkeleton && !showDetailError"
            v-b-modal.modal-link
            variant="outline-primary"
          >
            <i class="mc-fa icon-outline_link-2"></i>
            {{ $t('global.field.link') }}
          </b-button>
          <b-button
            class="ms-3"
            variant="outline-primary"
            @click="$router.push({ name: 'Tracking Temporary' })"
          >
            <i class="mc-fa icon-outline_arrow-left"></i>
            {{ $t('global.back') }}
          </b-button>
          <b-button
            :disabled="isSubmit || showDetailSkeleton || showDetailError"
            variant="primary"
            class="ms-2"
            @click="() => toSave()"
          >
            <i class="mc-fa icon-outline_save"></i> {{ $t('global.save') }}
          </b-button>
        </div>
      </div>

      <div v-if="showDetailSkeleton" class="row my-5">
        <div class="col-md-12">
          <div class="mc-list-info__top">
            <div v-for="item in 4" :key="item" class="__info-skeleton-item">
              <b-skeleton width="40%" class="mb-2" />
              <b-skeleton width="70%" />
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="showDetailError" class="row my-5">
        <div class="col-md-12">
          <b-card class="text-center py-4">
            <h5 class="mb-2">{{ $t('global.loader.timeoutTitle') }}</h5>
            <p class="mb-0">{{ $t('global.loader.timeoutSubtitle') }}</p>
          </b-card>
        </div>
      </div>

      <div v-else-if="id" class="row my-5">
        <div class="col-md-12">
          <div class="mc-list-info__top">
            <div v-if="hasCustomerContract">
              <span class="d-block">{{ $t('trackingTemp.form.customerName') }}</span>
              <strong>{{ customerName }}</strong>
            </div>

            <div v-if="hasCustomerContract">
              <span class="d-block">{{ $t('trackingTemp.form.contractNumber') }}</span>
              <strong>
                <router-link class="text-primary" :to="customerContractDetailRoute">
                  {{ customerContractNumber }}
                </router-link>
              </strong>
            </div>

            <div>
              <span class="d-block">{{ $t('trackingTemp.form.createdDate') }}</span>
              <strong>{{ createdOn }}</strong>
            </div>

            <div>
              <span class="d-block">{{ $t('trackingTemp.form.expiredDate') }}</span>
              <strong>{{ expiresOn }}</strong>
            </div>

            <div>
              <span class="d-block">Status</span>
              <strong>
                <span v-if="status === 'A'" class="mc-status mc-status--green">
                  {{ $t('global.active') }}
                </span>
                <span v-else-if="status === 'E'" class="mc-status mc-status--grey">
                  {{ $t('global.nonActive') }}
                </span>
              </strong>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-if="showDetailSkeleton">
      <h4 class="card-title">
        {{ $t('trackingTemp.form.configuration') }}
      </h4>
      <b-card>
        <b-row class="mb-3 pb-3 border-1 border-bottom">
          <b-col v-for="item in 3" :key="`config-${item}`" sm="12" md="3">
            <b-skeleton width="35%" class="mb-2" />
            <b-skeleton height="38px" />
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-skeleton width="55%" class="mb-3" />
          </b-col>
          <b-col md="6" class="text-end">
            <b-skeleton width="25%" class="ms-auto mb-3" />
          </b-col>
          <b-col md="6">
            <div class="border border-1 px-3 py-3">
              <b-skeleton v-for="item in 3" :key="`left-toggle-${item}`" height="24px" class="mb-3" />
            </div>
          </b-col>
          <b-col md="6">
            <div class="border border-1 px-3 py-3">
              <b-skeleton v-for="item in 2" :key="`right-toggle-${item}`" height="24px" class="mb-3" />
            </div>
          </b-col>
        </b-row>
      </b-card>

      <h4 class="card-title">Daftar Kendaraan & Kontak Penerima</h4>
      <b-card class="mc-card__setting">
        <b-skeleton height="220px" />
      </b-card>
    </template>

    <template v-else-if="showDetailError">
      <h4 class="card-title">
        {{ $t('trackingTemp.form.configuration') }}
      </h4>
      <b-card class="text-center py-4">
        <h5 class="mb-2">{{ $t('global.loader.timeoutTitle') }}</h5>
        <p class="mb-0">{{ $t('global.loader.timeoutSubtitle') }}</p>
      </b-card>
    </template>

    <form v-else ref="form">
      <h4 class="card-title">
        {{ $t('trackingTemp.form.configuration') }}
      </h4>
      <b-card>
        <b-row class="mb-3 pb-3 border-1 border-bottom">
          <b-col sm="12" md="3">
            <label class="required mb-1">{{ $t('trackingTemp.form.linkName') }}</label>
            <b-form-input
              id="duration"
              v-model="form.name"
              trim
              :placeholder="$t('trackingTemp.form.linkName')"
              :class="{ 'is-invalid': $v.form.name.$error }"
            />
            <div v-if="$v.form.name.$error" class="invalid-feedback">
              <span v-if="!$v.form.name.required">
                {{ $t('trackingTemp.form.linkName') }}
                {{ $t('global.validation.required') }}
              </span>
            </div>
          </b-col>
          <b-col v-if="!hasCustomerContract" sm="12" md="3">
            <label class="required mb-1">{{ $t('trackingTemp.form.duration') }}</label>
            <b-row>
              <b-col>
                <b-form-input
                  id="duration"
                  v-model="form.duration"
                  trim
                  placeholder="0"
                  :class="{
                    'is-invalid': $v.form.duration.$error || (!minDuration && isSubmitValidate),
                  }"
                  type="number"
                  min="1"
                />
                <b-form-invalid-feedback v-if="!minDuration">
                  {{ $t('trackingTemp.validation.validateMinDuration') }}
                </b-form-invalid-feedback>
                <div v-if="$v.form.duration.$error" class="invalid-feedback">
                  <span v-if="!$v.form.duration.required">
                    {{ $t('trackingTemp.form.duration') }}
                    {{ $t('global.validation.required') }}
                  </span>
                </div>
              </b-col>
              <b-col>
                <multiselect
                  v-model="duration"
                  :options="['Menit', 'Jam', 'Hari', 'Minggu', 'Bulan']"
                  placeholder="Durasi"
                />
              </b-col>
            </b-row>
          </b-col>
          <b-col sm="12" md="3">
            <b-form-group :label="$t('trackingTemp.form.note')" label-for="note">
              <b-input-group>
                <b-form-input
                  id="note"
                  v-model="form.note"
                  trim
                  :placeholder="$t('trackingTemp.form.note')"
                />
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col v-if="hasCustomerContract" sm="12" md="6" />

          <!-- Create Contact Button -->
          <b-col v-if="!hasCustomerContract" sm="12" md="3">
            <div class="__create-contact-btn">
              <McButton
                variant="secondary"
                text="Kontak Penerima"
                icon="icon-outline_person-add"
                :disabled="isLoading || isSubmit"
                @click="handleOpenModalContactForm"
              />
              <div class="__help-text">*Otomatis kirim link/notifikasi via WA</div>
            </div>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <p class="mc-paragraph">
              {{ $t('trackingTemp.form.informationDisplay') }}
              <span
                v-b-tooltip.topright
                class="ms-1"
                title="Anda dapat mengatur informasi apa saja yang ditampilkan pada kendaraan"
              >
                <i class="icon-outline_info"></i>
              </span>
            </p>
          </b-col>
          <b-col md="6" class="text-end">
            <strong v-if="!cekNonaktifInfoFleet.all" @click="checkAllInfoFleet">
              <a class="text-primary">{{ $t('trackingTemp.form.activateAll') }}</a>
            </strong>
            <strong v-else @click="checkAllInfoFleet">
              <a class="text-primary"> {{ $t('trackingTemp.form.nonActivateAll') }}</a>
            </strong>
          </b-col>
          <b-col md="6">
            <div class="border border-1 px-3 py-3">
              <div class="clearfix mb-2">
                <span class="float-start">{{ $t('global.object.driverName') }}</span>
                <div class="form-check form-switch form-switch-md float-end">
                  <input
                    v-model="cekNonaktifInfoFleet.driverName"
                    class="form-check-input"
                    type="checkbox"
                  />
                </div>
              </div>
              <div class="clearfix mb-2">
                <span class="float-start">{{ $t('global.text.lastSeen') }}</span>
                <div class="form-check form-switch form-switch-md float-end">
                  <input
                    v-model="cekNonaktifInfoFleet.lastSeen"
                    class="form-check-input"
                    type="checkbox"
                  />
                </div>
              </div>

              <div class="clearfix">
                <span class="float-start">{{ $t('global.object.speed') }}</span>
                <div class="form-check form-switch form-switch-md float-end">
                  <input
                    v-model="cekNonaktifInfoFleet.speed"
                    class="form-check-input"
                    type="checkbox"
                  />
                </div>
              </div>
            </div>
          </b-col>
          <b-col md="6">
            <div class="border border-1 px-3 py-3">
              <div class="clearfix mb-2">
                <span class="float-start">{{ $t('global.alert.temperature') }}</span>
                <div class="form-check form-switch form-switch-md float-end">
                  <input
                    v-model="cekNonaktifInfoFleet.temperature"
                    class="form-check-input"
                    type="checkbox"
                  />
                </div>
              </div>
              <div class="clearfix">
                <span class="float-start">{{ $t('global.object.machineStatus') }}</span>
                <div class="form-check form-switch form-switch-md float-end">
                  <input
                    v-model="cekNonaktifInfoFleet.engineStatus"
                    class="form-check-input"
                    type="checkbox"
                  />
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <h4 class="card-title">Daftar Kendaraan & Kontak Penerima</h4>

      <b-card class="mc-card__setting">
        <b-tabs v-model="tabIndex" content-class="p-4">
          <b-tab>
            <template #title>
              <span class="d-none d-sm-inline-block">Kendaraan</span>
            </template>
            <FleetList
              :readonly="hasCustomerContract"
              :showSequenceColumn="hasCustomerContract"
              :vehicleIds="form.vehicleIds"
              @update:vehicleIds="form.vehicleIds = $event"
            />
          </b-tab>

          <b-tab @click="handleClickContactTab">
            <template #title>
              <span class="d-none d-sm-inline-block">Kontak Penerima</span>
            </template>
            <ContactList
              :isContractTempLv="hasCustomerContract"
              :readonly="hasCustomerContract"
              :isLoading="isLoading || isSubmit"
              :items="displayContacts"
              @update:items="form.contacts = $event"
            />
          </b-tab>
        </b-tabs>
      </b-card>
    </form>

    <b-modal
      id="modal-link"
      class="mc-modal"
      :title="$t('global.field.link')"
      size="md"
      modal-class="mc-modal-link"
      centered
      scrollable
    >
      <b-list-group v-if="status != 'E'">
        <b-list-group-item>
          <i>{{ tempLiveViewLink }}</i>
        </b-list-group-item>
      </b-list-group>
      <b-list-group v-else>
        <b-list-group-item class="mc-modal-link_expired">
          <i class="mdi mdi-close-circle-outline"></i>{{ $t('global.alert.text.expiredLink') }}
        </b-list-group-item>
      </b-list-group>
      <template #modal-footer="{ cancel }">
        <b-row v-if="status != 'E'" class="mc-modal__filter-footer mx-0">
          <b-col>{{ $t('global.alert.text.activeLinkUntil') }} : {{ expiresOn }}</b-col>
          <b-col sm="12" md="12" class="mc-modal__filter-footer-btn">
            <b-button variant="outline-primary" class="me-2" @click="cancel()">
              {{ $t('global.action.close') }}
            </b-button>
            <a class="btn btn-primary" @click="copyLink">{{ $t('global.action.copyLink') }}</a>
          </b-col>
        </b-row>
        <b-row v-else class="mc-modal__filter-footer mx-0">
          <b-col sm="12" md="12" class="mc-modal__filter-footer-btn">
            <b-button variant="outline-primary" @click="cancel()">
              {{ $t('global.action.close') }}
            </b-button>
          </b-col>
        </b-row>
      </template>
    </b-modal>

    <ModalContactForm ref="modal-contact-form" @create:contact="handleAddContact" />
    <ModalSuccessCreate
      ref="modal-success-create"
      :link="tempLiveViewLink"
      :isLoading="isSubmit"
      @create:contact="($event) => handleAddContact($event, true)"
    />
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import { MixPanel } from '@/mixin/mixpanel'

import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header'
import Multiselect from 'vue-multiselect'
import McButton from '@/components/McButton'
import ModalContactForm from './components/modals/ModalContactForm'
import ModalSuccessCreate from './components/modals/ModalSuccessCreate'

import FleetList from './sections/FleetList'
import ContactList from './sections/ContactList'

import { Helper } from '@/mixin/helper'
import {
  CREATE_TRACKING_TEMPORARY,
  TRACKING_TEMPORARY_DETAIL,
  UPDATE_TRACKING_TEMPORARY,
} from '@/services/api/trackingTemporary/temporary'
import { GOOGLE_MAP_ADDRESS_TYPE, MASTER_DATA_ADDRESS_TYPE } from './lib/constants/types'

import { required, requiredIf } from 'vuelidate/lib/validators'
import Swal from 'sweetalert2'
import { parsePhoneNumber } from 'libphonenumber-js'

/**
 * Starter component
 */
export default {
  page: {
    title: 'Pelacakan Sementara',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    Multiselect,
    McButton,
    ModalContactForm,
    ModalSuccessCreate,

    FleetList,
    ContactList,
  },
  mixins: [Helper, MixPanel],
  apollo: {
    temporaryLiveView: {
      query: TRACKING_TEMPORARY_DETAIL,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          filter: {
            temporaryLvId: parseInt(this.id),
          },
        }
      },
      // Disable the query
      skip() {
        return this.$route.params.id === undefined
      },
      result({ data, loading }) {
        if (!loading) {
          if (data.temporaryLiveView.isSuccessful === false) {
            this.$mcToast.error(data.temporaryLiveView.message)
            return
          }

          this.isDetailLoadFailed = false
          this.initEditPage(data.temporaryLiveView.temporaryLiveView)
        }
      },
      error() {
        this.isDetailLoading = false
        this.isDetailLoadFailed = true
      },
    },
  },
  data() {
    return {
      isDetailLoadFailed: false,
      isDetailLoading: Boolean(this.$route.params.id),
      isLoading: false,
      tabIndex: 0,
      duration: 'Hari',
      cekNonaktifInfoFleet: {
        all: true,
        driverName: true,
        lastSeen: true,
        speed: true,
        temperature: true,
        engineStatus: true,
      },
      form: {
        duration: 1,
        name: null,
        note: null,
        pickedColumns: [],
        alertCodes: [],
        vehicleIds: [],

        contacts: [],
      },
      formOld: {
        duration: 1,
        name: null,
        note: null,
        pickedColumns: [],
        alertCodes: [],
        vehicleIds: [],
        durationOption: 'Menit',

        contacts: [],
      },
      baseUrl: window.location.origin,
      createdOn: null,
      expiresOn: null,
      status: 'A',
      customerContract: null,
      accessToken: null,
      shortKey: null,
      isSubmit: false,
      isSubmitValidate: false,
    }
  },

  validations() {
    return {
      form: {
        name: { required },
        duration: {
          required: requiredIf(function () {
            return !this.hasCustomerContract
          }),
        },
      },
    }
  },

  computed: {
    id() {
      return this.$route.params.id || 0
    },

    title() {
      return !this.id
        ? `${this.$t('trackingTemp.new')}`
        : `${this.$t('menuitems.trackingTemp.text')}`
    },

    isEditMode() {
      return Boolean(this.$route.params.id)
    },

    showDetailSkeleton() {
      return this.isEditMode && this.isDetailLoading
    },

    showDetailError() {
      return this.isEditMode && this.isDetailLoadFailed && !this.isDetailLoading
    },

    hasCustomerContract() {
      return Boolean(this.customerContract?.uuid)
    },

    customerContractNumber() {
      return this.customerContract?.number || '-'
    },

    customerName() {
      return this.customerContract?.customer?.name || '-'
    },

    customerContractDetailRoute() {
      return {
        name: 'Master Customer Contract Detail',
        params: { contractId: this.customerContract?.uuid },
      }
    },

    contractEmailRows() {
      return (this.customerContract?.customer?.contacts || []).map((contact) => ({
        email: contact?.email || '-',
      }))
    },

    displayContacts() {
      return this.hasCustomerContract ? this.contractEmailRows : this.form.contacts
    },

    pickedColumns() {
      let picked = []
      Object.keys(this.cekNonaktifInfoFleet).forEach((item) => {
        if (item != 'all') {
          if (this.cekNonaktifInfoFleet[item] === true) {
            picked.push(item)
          }
        }
      })

      return picked
    },

    tempLiveViewLink() {
      const token = this.shortKey ? `shToken=${this.shortKey}` : `token=${this.accessToken}`
      return `${this.baseUrl}/fleet/live-track/${this.id}?${token}`
    },

    minDuration() {
      if (this.hasCustomerContract) return true

      return this.form.duration > 0
    },
  },

  watch: {
    id(newId, oldId) {
      if (newId && newId !== oldId) {
        this.isDetailLoading = true
        this.isDetailLoadFailed = false
      }

      if (!newId) {
        this.isDetailLoading = false
        this.isDetailLoadFailed = false
      }
    },
  },

  methods: {
    initEditPage(data) {
      this.isLoading = true
      this.isDetailLoadFailed = false

      let duration = this.calculateMinutes(data.expiresOn, data.createdOn)
      let durationOptions = this.getDurationOption(duration - 1)
      this.form.name = data.name
      this.form.note = data.note
      this.form.duration = durationOptions.value
      this.duration = durationOptions.option

      Object.keys(this.cekNonaktifInfoFleet).forEach((key) => {
        if (key != 'all') {
          if (data.pickedColumns?.includes(key)) {
            this.cekNonaktifInfoFleet[key] = true
          } else {
            this.cekNonaktifInfoFleet[key] = false
          }
        }
      })

      this.createdOn = this.momentDMHms(data.createdOn)
      this.expiresOn = this.momentDMHms(data.expiresOn)
      this.status = data.status
      this.customerContract = data.customerContract
      this.accessToken = data.accessToken
      this.shortKey = data.shortKey
      this.form.vehicleIds = data.vehicleIds?.map((item) => item.vehicleId)

      this.formOld.name = data.name
      this.formOld.note = data.note
      this.formOld.duration = durationOptions.value
      this.formOld.durationOptions = durationOptions.option
      this.formOld.vehicleIds = data.vehicleIds?.map((item) => item.vehicleId)
      this.formOld.pickedColumns = data.pickedColumns

      if (data.contacts?.length > 0) {
        const formattedContacts = []
        const formattedOldContacts = []

        data.contacts.forEach((item) => {
          let location = null

          if (item.location !== null) {
            location = {
              name: item.location.name,
              fullName: item.location.fullName,
              lat: item.location.address.lat,
              lng: item.location.address.lng,
            }

            if (item.location.id.masterDataValue) {
              location.id = item.location.id.masterDataValue.toString()
              location.type = MASTER_DATA_ADDRESS_TYPE
            } else if (item.location.id.googleValue) {
              location.id = item.location.id.googleValue
              location.type = GOOGLE_MAP_ADDRESS_TYPE
            } else if (!item.location.id?.googleValue) {
              // case if the location is free pick from maps
              location.type = GOOGLE_MAP_ADDRESS_TYPE
            }
          }

          const newData = {
            contactId: item.contactId,
            label: item.label,
            phoneNumber: parsePhoneNumber(item.phoneNumber, "ID")?.format('E.164') ?? item.phoneNumber,
            location: location === null ? location : { ...location },
            createdOn: item.createdOn,
            createdBy: item.createdBy,
          }

          const oldData = {
            label: item.label,
            phoneNumber: parsePhoneNumber(item.phoneNumber, "ID")?.format('E.164') ?? item.phoneNumber,
            location: location === null ? location : { ...location },
          }

          formattedContacts.push(newData)
          formattedOldContacts.push(oldData)
        })


        this.$set(this.form, 'contacts', [...formattedContacts])
        this.$set(this.formOld, 'contacts', [...formattedOldContacts])
      }

      this.isLoading = false
      this.isDetailLoading = false
    },

    checkAllInfoFleet() {
      this.cekNonaktifInfoFleet.all = !this.cekNonaktifInfoFleet.all
      let obj = { ...this.cekNonaktifInfoFleet }
      let param = this.cekNonaktifInfoFleet.all
      Object.keys(obj).forEach(function (key) {
        if (key !== 'all') {
          obj[key] = param
        }
      })
      this.cekNonaktifInfoFleet = obj
    },

    handleClickContactTab() {
      this.track('Pelacakan Sementara Kontak Penerima Clicked')
    },

    handleAddContact(item, immediateSave = false) {
      const pn = parsePhoneNumber(item.phoneNumber, 'ID')
      if (!pn || !pn.isValid()) {
        this.$refs['modal-contact-form'].SetFormErrors({
          errorName: 'PhoneNumberInvalid',
        })

        return
      }
      const formattedPhoneNumber = pn.format('E.164')
      

      const hasSamePhoneNumber = this.form.contacts.find(
        (contact) => contact.phoneNumber === formattedPhoneNumber
      )

      if (hasSamePhoneNumber && !immediateSave) {
        this.$refs['modal-contact-form'].SetFormErrors({
          errorName: 'PhoneNumberAlreadyExists',

        })

        return
      }

      const newContacts = [
        ...this.form.contacts,
        { ...item, phoneNumber: formattedPhoneNumber, contactId: Date.now().toString() },
      ]
      this.$set(this.form, 'contacts', newContacts)

      if (immediateSave) {
        this.toSave(true)
        return
      }

      this.$bvModal.hide('modal-contact-form')
      this.tabIndex = 1
    },

    toSave(isAddContact = false) {
      this.track('Pelacakan Sementara Save Clicked')

      this.isSubmitValidate = true

      this.$v.$touch()
      if (this.$v.$error || !this.minDuration) {
        return
      }

      this.isSubmit = true

      const isContractUpdate = this.hasCustomerContract && this.$route.params.id !== undefined

      if (!isContractUpdate && this.form.vehicleIds.length == 0) {
        this.isSubmit = false
        return Swal.fire({
          title: this.$t('global.alert.text.failedSaved'),
          text: this.$t('global.alert.text.fleetMinimum'),
          imageUrl: require(`@/assets/images/alert-report-danger.png`),
          cancelButtonText: this.$t('global.action.close'),
          confirmButtonText: this.$t('global.object.addFleet'),
          showCloseButton: true,
          showCancelButton: true,
        }).then((result) => {
          if (result.isConfirmed) {
            this.$bvModal.show('modal-add-fleet')
          }
        })
      }

      if (this.pickedColumns.length == 0) {
        this.isSubmit = false
        return Swal.fire({
          title: this.$t('global.alert.text.failedSaved'),
          text: this.$t('global.alert.text.fleetInfoMinimum'),
          imageUrl: require(`@/assets/images/alert-report-danger.png`),
          cancelButtonText: this.$t('global.action.close'),
          showConfirmButton: false,
          showCancelButton: true,
        })
      }

      let input = {
        name: this.form.name,
        note: this.form.note,
        pickedColumns: this.pickedColumns,
      }

      if (!isContractUpdate) {
        let duration = this.durationToMinutes(this.form.duration, this.duration)

        let vehicleIds = []
        this.form.vehicleIds.forEach(function (item) {
          vehicleIds.push(parseInt(item))
        })

        // format form.contacts
        let contacts = []
        if (this.form.contacts.length > 0) {
          this.form.contacts.forEach((contact) => {
            const newData = {
              ...contact,
            }

            // skip if contact has no location
            if (contact.location) {
              newData.location = { ...contact.location }
            }

            // delete contactId before send to API
            delete newData.contactId
            delete newData.createdBy
            delete newData.createdOn

            contacts.push(newData)
          })
        }

        input = {
          ...input,
          duration: duration,
          vehicleIds,
          contacts,
        }
      }

      if (this.$route.params.id !== undefined || isAddContact) {
        let formOld = {
          name: this.formOld.name,
          note: this.formOld.note,
          pickedColumns: this.formOld.pickedColumns,
        }

        if (!isContractUpdate) {
          let durationOld = this.durationToMinutes(this.formOld.duration, this.formOld.durationOption)
          formOld = {
            ...formOld,
            duration: durationOld,
            vehicleIds: this.formOld.vehicleIds,
            contacts: this.formOld.contacts,
          }
        }

        this.$apollo
          .mutate({
            mutation: UPDATE_TRACKING_TEMPORARY,
            variables: {
              input: {
                temporaryLvId: parseInt(this.id),
                oldData: formOld,
                newData: input,
              },
            },
          })
          .then(async (response) => {
            if (response.data.updateTemporaryLiveView.isSuccessful) {
              // refetch data to get the contactId
              await this.$apollo.queries.temporaryLiveView.refetch()

              this.isSubmit = false
              this.$mcToast.success(`Data Pelacakan Sementara berhasil tersimpan`)

              if (isAddContact) {
                this.$refs['modal-success-create'].SetModalType('success-submit')
              }
            } else {
              if (isAddContact) {
                this.form.contacts.pop()
              }

              this.isSubmit = false
              return this.$mcToast.error(response.data.updateTemporaryLiveView.message)
            }
          })
      } else {
        this.$apollo
          .mutate({
            mutation: CREATE_TRACKING_TEMPORARY,
            variables: {
              input: input,
            },
          })
          .then(async (response) => {
            if (response.data.createTemporaryLiveView.isSuccessful) {
              // refetch data to get the contactId
              await this.$apollo.queries.temporaryLiveView.refetch()
              this.isSubmit = false

              const id = response.data.createTemporaryLiveView.temporaryLiveView.temporaryLvId
              this.$router.push({ name: 'Tracking Temporary Update', params: { id } })

              if (this.form.contacts.length === 0) {
                this.$refs['modal-success-create'].SetModalType('form')
                this.$bvModal.show('modal-success-create')
                return
              } else {
                this.$bvModal.show('modal-success-create')
              }
            } else {
              this.isSubmit = false
              return this.$mcToast.error(response.data.createTemporaryLiveView.message)
            }
          })
      }
    },

    copyLink() {
      navigator.clipboard.writeText(this.tempLiveViewLink)
      this.$mcToast.success(`Tautan berhasil disalin`)
      this.$bvModal.hide('modal-link')
    },

    handleOpenModalContactForm() {
      this.$bvModal.show('modal-contact-form')
    },
  },
}
</script>

<style lang="scss" scoped>
.mc-list-info__top {
  .__info-skeleton-item {
    flex: 1 1 180px;
  }
}

.__create-contact-btn {
  margin-top: 23.5px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  .__help-text {
    font-size: 12px;
    font-style: italic;
    color: $mep-color-text-300;
  }
}
</style>
